import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { useEffect, useMemo, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, ButtonGroup } from 'stories/components'

import { McrDispositions } from './Dispositions'
import { McrFcAccounts } from './FcAccounts'
import { McrFiler } from './Filer'
import { defaultMcrConfig, IMcrAdminView, IMcrConfig } from './types'
import { McrXmlFieldMap } from './XmlFieldMap'

const tabs: Record<string, string> = {
  filer: 'Filer & States',
  dispositions: 'Application Dispositions',
  fcAccounts: 'Financial Condition Accounts',
  xmlFieldMap: 'XML Field Map',
}

/**
 * Configuration for the NMLS Mortgage Call Report.
 *
 * Four things a licensee has to tell the platform and nothing can be derived: who they are on
 * their NMLS record, which states they file an RMLA for, which of their own loan statuses is a
 * denial, and which of their own ledger accounts is a warehouse line. Everything else the report
 * works out and shows its working for.
 *
 * The whole config saves as one object, so the tabs edit a single draft and one Save writes it.
 */
export const MortgageCallReportConfiguration = () => {
  const [isLoading, setLoading] = useState(true)
  const [isChanged, setChanged] = useState(false)
  const [tab, setTab] = useState('filer')
  const [view, setView] = useState<IMcrAdminView | null>(null)
  const [config, setConfig] = useState<IMcrConfig>(defaultMcrConfig())

  const applyView = (next: IMcrAdminView) => {
    setView(next)
    setConfig({ ...defaultMcrConfig(), ...(next?.config || {}) })
  }

  useEffect(() => {
    getAdminConfig('mortgageCallReport')
      .then((res: IMcrAdminView) => res && applyView(res))
      .finally(() => setLoading(false))
  }, [])

  const onChange = (next: IMcrConfig) => {
    setConfig(next)
    setChanged(true)
  }

  const onSubmit = async () => {
    setLoading(true)
    try {
      // The endpoint returns the re-resolved view, so readiness, effective dispositions and the
      // inferred account lines all refresh from the values that were actually stored.
      const res: IMcrAdminView = await setAdminConfig('mortgageCallReport', cloneDeep(config) as any)
      if (res?.config) applyView(res)
      setChanged(false)
    } finally {
      setLoading(false)
    }
  }

  const content = useMemo(() => {
    if (!view) return null
    switch (tab) {
      case 'dispositions':
        return <McrDispositions view={view} config={config} onChange={onChange} />
      case 'fcAccounts':
        return <McrFcAccounts view={view} config={config} onChange={onChange} />
      case 'xmlFieldMap':
        return <McrXmlFieldMap view={view} config={config} onChange={onChange} />
      default:
        return <McrFiler view={view} config={config} onChange={onChange} />
    }
  }, [tab, view, config])

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />

      <div className="mb-4 border-b-2 border-shade-blue w-full italic flex justify-between items-center">
        <p>- NMLS Mortgage Call Report</p>
      </div>
      <p className="mb-4 text-sm text-gray-600">
        The RMLA and Financial Condition reports, and the NMLS upload file, are generated from this
        configuration. Run them from Servicing Reports under Regulatory Filings.
      </p>

      <ButtonGroup title={Object.values(tabs)} value={tabs[tab]} onChange={(value) => {
        const key = Object.keys(tabs).find((k) => tabs[k] === value)
        if (key) setTab(key)
      }} />

      <div className="mt-4">{content}</div>

      <div className="mt-6">
        <Button onClick={onSubmit} disabled={!isChanged} loading={isLoading}>
          Save
        </Button>
      </div>

      <SaveChanges show={isChanged} onSave={onSubmit} />
    </div>
  )
}
